import { useEffect, useRef, useState } from 'react';
import { energyFeaturesData, carbonFeaturesData } from '@/mocks/homeData';

type FeatureItem = {
  icon: string;
  title: string;
  description: string;
  href: string;
};

function FeatureCard({ feature, index }: { feature: FeatureItem; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * 8, y: -x * 8 });
  };

  const handleMouseLeave = () => {
    setTilt({ x: 0, y: 0 });
    setIsHovered(false);
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={`group bg-firmai-card border border-firmai-border rounded-xl p-5 md:p-6 hover:border-firmai-green/40 cursor-pointer relative overflow-hidden transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{
        transitionDelay: `${index * 70}ms`,
        transform: isHovered
          ? `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(1.02)`
          : 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
        transition: isHovered
          ? 'transform 0.15s ease-out, border-color 0.3s ease'
          : `transform 0.4s ease-out, border-color 0.3s ease, opacity 0.5s ease ${index * 70}ms, translate 0.5s ease ${index * 70}ms`,
      }}
    >
      {/* Glow effect */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(400px circle at ${(tilt.y + 4) * 12.5}% ${(tilt.x + 4) * 12.5}%, rgba(0,217,163,0.07), transparent 50%)`,
        }}
      />

      {/* Icon */}
      <div className="relative w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-firmai-green/20 to-firmai-blue/20 border border-firmai-green/20 mb-4 group-hover:border-firmai-green/40 group-hover:from-firmai-green/30 transition-all duration-300">
        <span className="w-6 h-6 flex items-center justify-center text-firmai-green group-hover:scale-110 transition-transform duration-300">
          <i className={`${feature.icon} text-lg`} />
        </span>
      </div>

      <h3 className="relative text-white font-semibold text-sm md:text-base mb-2.5 group-hover:text-firmai-green transition-colors duration-300 leading-snug">
        {feature.title}
      </h3>

      <p className="relative text-firmai-text text-xs md:text-sm leading-relaxed mb-4">
        {feature.description}
      </p>

      <a
        href={feature.href}
        onClick={(e) => {
          const href = (e.currentTarget as HTMLAnchorElement).getAttribute('href');
          if (href && href.startsWith('#')) {
            e.preventDefault();
            document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        className="relative inline-flex items-center gap-1 text-firmai-green text-xs font-medium hover:gap-2 transition-all duration-200 group/link"
      >
        了解详情
        <span className="w-3.5 h-3.5 flex items-center justify-center group-hover/link:translate-x-1 transition-transform duration-200">
          <i className="ri-arrow-right-line" />
        </span>
      </a>
    </div>
  );
}

function GroupHeader({ label, title, desc, isVisible }: { label: string; title: string; desc: string; isVisible: boolean }) {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-firmai-green/10 border border-firmai-green/25 text-firmai-green text-xs font-semibold mb-2">
          {label}
        </span>
        <h3 className="text-white font-bold text-lg md:text-2xl">{title}</h3>
        <p className="text-firmai-muted text-xs md:text-sm mt-1 max-w-xl">{desc}</p>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const energyHeaderRef = useRef<HTMLDivElement>(null);
  const carbonHeaderRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [energyHeaderVisible, setEnergyHeaderVisible] = useState(false);
  const [carbonHeaderVisible, setCarbonHeaderVisible] = useState(false);

  useEffect(() => {
    const createObserver = (setter: (v: boolean) => void) =>
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setter(true);
            }
          });
        },
        { threshold: 0.1 }
      );

    const mainObserver = createObserver(setHeaderVisible);
    const energyObserver = createObserver(setEnergyHeaderVisible);
    const carbonObserver = createObserver(setCarbonHeaderVisible);

    const mainHeader = sectionRef.current?.querySelector('.main-header');
    if (mainHeader) mainObserver.observe(mainHeader);
    if (energyHeaderRef.current) energyObserver.observe(energyHeaderRef.current);
    if (carbonHeaderRef.current) carbonObserver.observe(carbonHeaderRef.current);

    return () => {
      mainObserver.disconnect();
      energyObserver.disconnect();
      carbonObserver.disconnect();
    };
  }, []);

  return (
    <section
      id="features"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-firmai-dark overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,180,216,0.2) 1px, transparent 0)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Decorative glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-64 bg-firmai-green/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <div
          className={`main-header text-center mb-14 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-firmai-green/10 border border-firmai-green/25 text-firmai-green text-xs font-semibold tracking-wider">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-government-line" />
            </span>
            工信部数字化能碳管理中心建设指南 · 12项核心功能全覆盖
          </span>
          <h2 className="font-display font-bold text-2xl md:text-4xl text-white mt-4">
            工业能碳数字化管理，一个平台全搞定
          </h2>
          <p className="text-firmai-text mt-4 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
            专注工业智能化赋能，严格对标工信部建设指南，完整覆盖能源管理与碳排放管理两大模块的全部 12 项功能，助力工业企业和园区高效通过能碳管理中心建设评估
          </p>

          {/* 12-count badge */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            {[
              '能耗查询', '能源消费分析', '能效对标', '能流分析',
              '能效平衡优化', '用能预算管理', '碳排放核算', '产品碳足迹',
              '供应链碳管理', '碳排放预算', '碳核查支撑', '碳资产管理',
            ].map((tag, i) => (
              <span
                key={tag}
                className="flex items-center gap-1 px-2.5 py-1 text-xs bg-white/5 text-firmai-text rounded-full border border-white/10 hover:border-firmai-green/30 hover:text-firmai-green transition-all duration-300"
              >
                <span className="w-4 h-4 flex items-center justify-center text-firmai-green opacity-70">
                  <i className="ri-check-line text-xs" />
                </span>
                {tag}
                <span className="text-firmai-muted ml-0.5">{i + 1 < 10 ? `0${i + 1}` : `${i + 1}`}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Divider between groups */}
        <div className="mb-12">
          {/* Group 1: Energy Management */}
          <div ref={energyHeaderRef}>
            <GroupHeader
              label="能源管理模块 · 06项功能"
              title="精准能耗监测与用能优化"
              desc="从实时采集到智能分析，全链路覆盖工业企业能源管理核心场景"
              isVisible={energyHeaderVisible}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-14">
            {energyFeaturesData.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} index={index} />
            ))}
          </div>

          {/* Separator */}
          <div
            className={`flex items-center gap-4 mb-12 transition-all duration-700 ${
              carbonHeaderVisible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-firmai-border to-transparent" />
            <div className="flex items-center gap-2 px-4 py-1.5 bg-firmai-card border border-firmai-border rounded-full">
              <span className="w-4 h-4 flex items-center justify-center text-firmai-green">
                <i className="ri-leaf-line text-sm" />
              </span>
              <span className="text-firmai-muted text-xs">碳排放管理模块</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-firmai-border to-transparent" />
          </div>

          {/* Group 2: Carbon Management */}
          <div ref={carbonHeaderRef}>
            <GroupHeader
              label="碳排放管理模块 · 06项功能"
              title="全链路碳核算与碳资产管理"
              desc="从碳排放核算到碳资产变现，完整支撑企业双碳目标落地与合规履约"
              isVisible={carbonHeaderVisible}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {carbonFeaturesData.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} index={index} />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 ${
            carbonHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <a
            href="#demo"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-7 py-3 bg-firmai-green text-firmai-dark font-semibold rounded-md hover:bg-firmai-green/90 hover:scale-105 active:scale-95 transition-all duration-200 whitespace-nowrap group"
          >
            申请建设方案咨询
            <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
              <i className="ri-arrow-right-line" />
            </span>
          </a>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 px-7 py-3 border border-firmai-border text-firmai-text font-medium rounded-md hover:border-firmai-green/40 hover:text-white transition-all duration-200 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-play-circle-line" />
            </span>
            查看实时演示大屏
          </a>
        </div>
      </div>
    </section>
  );
}